import { Container, Typography } from '@cleartrip/ct-design-components'
import { AnimatePresence, motion } from 'framer-motion'
import { memo, type ReactNode, useEffect, useRef } from 'react'

import ChevronDown from '@/assets/svg/ChevronDown'

import { FLIGHTS_AT_SAME_PRICE, HIDE_SAME_PRICE_OPTIONS, samePriceMoreOptionsLabel } from '../config'
import ExpandableTupleGroup, { scrollAwareCollapse } from './ExpandableTupleGroup'
import TupleGroupFooter from './TupleGroupFooter'
import { SAME_PRICE_THEME, TUPLE_COLORS } from '../config/tupleTheme'

export interface SamePriceGroupProps {
  children: ReactNode
  count: number
  expanded: boolean
  onExpand: () => void
  onCollapse: () => void
  siblings?: ReactNode
}

const SamePriceGroup: React.FC<SamePriceGroupProps> = ({
  children,
  count,
  expanded,
  onExpand,
  onCollapse,
  siblings = null,
}) => {
  const groupRef = useRef<HTMLDivElement>(null)
  const collapseTimerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    return () => {
      const timer = collapseTimerRef.current
      if (timer) clearTimeout(timer)
    }
  }, [])

  const handleCollapse = () => scrollAwareCollapse(groupRef.current, onCollapse, collapseTimerRef)

  const handleHeaderKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleCollapse()
    }
  }

  const header = (
    <AnimatePresence initial={false}>
      {expanded && (
        <motion.div
          key='same-price-header'
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          initial={{ opacity: 0, height: 0 }}
          style={{ overflow: 'hidden' }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        >
          <Container
            aria-expanded={expanded}
            className='flex flex-middle px-2 pt-1 pb-3 w-100p c-pointer'
            role='button'
            tabIndex={0}
            onClick={handleCollapse}
            onKeyDown={handleHeaderKeyDown}
          >
            <Typography color='heading' variant='HM4'>
              {FLIGHTS_AT_SAME_PRICE}
            </Typography>
            <motion.div
              animate={{ rotate: 180 }}
              className='ml-2 flex flex-middle'
              style={{ height: 14, width: 14 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <ChevronDown color={TUPLE_COLORS.heading} strokeWidth='1.5' />
            </motion.div>
          </Container>
        </motion.div>
      )}
    </AnimatePresence>
  )

  return (
    <ExpandableTupleGroup
      active={count > 0}
      collapsedFooter={
        count > 0 ? (
          <TupleGroupFooter label={samePriceMoreOptionsLabel(count)} variant='expand' onClick={onExpand} />
        ) : null
      }
      expanded={expanded}
      expandedFooter={<TupleGroupFooter label={HIDE_SAME_PRICE_OPTIONS} variant='collapse' onClick={handleCollapse} />}
      groupRef={groupRef}
      header={header}
      siblings={siblings}
      theme={SAME_PRICE_THEME}
    >
      {children}
    </ExpandableTupleGroup>
  )
}

export default memo(SamePriceGroup)
